import i18n from "@/i18n";
/**
 * VVOpeningStateDialog — TES-B2C1B1 §22-§26.
 * Append-only opening VV state via the canonical
 * POST /workforce/employees/{id}/vv/opening-state. Explicit Save.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { browserLocalYMD, hoursMinutesToMinutes } from "./workforceHelpers";

export function buildOpeningStatePayload({
    asOfDate, earningYear, hours, minutes, carriedCount, reason,
    isRevision, sourceKey,
}) {
    const carried = [];
    for (let i = 1; i <= (carriedCount || 0); i += 1) {
        carried.push({ earning_year: earningYear - 1, ordinal: i });
    }
    return {
        source_key: sourceKey,
        is_revision: Boolean(isRevision),
        as_of_date: asOfDate,
        earning_year: earningYear,
        eligible_minutes_ytd: hoursMinutesToMinutes(hours, minutes),
        carried_entitlements: carried,
        reason: (reason || "").trim() || undefined,
    };
}

const newSourceKey = () =>
    `opening-ui-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const intOrNaN = (raw) => {
    if (raw === "") return 0;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : NaN;
};

const initial = () => {
    const today = browserLocalYMD();
    return { asOfDate: today, earningYear: Number(today.slice(0, 4)),
        hours: 0, minutes: 0, carriedCount: 0, reason: "" };
};

function NumberField({ label, testId, value, onChange, max, hint }) {
    return (
        <label className="block text-sm">
            <span className="text-neutral-700">{label}</span>
            <Input type="number" inputMode="numeric" min={0} max={max}
                step={1} className="mt-1" data-testid={testId}
                value={value}
                onChange={(e) => {
                    const n = intOrNaN(e.target.value);
                    if (Number.isNaN(n) || (max != null && n > max)) return;
                    onChange(n);
                }} />
            {hint && (
                <span className="mt-1 block text-xs text-neutral-500">
                    {hint}
                </span>
            )}
        </label>
    );
}

export default function VVOpeningStateDialog({
    open, onOpenChange, employeeId, isRevision, onSaved,
}) {
    const [s, setS] = useState(initial);
    const [sourceKey, setSourceKey] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");
    const set = (patch) => setS((p) => ({ ...p, ...patch }));

    useEffect(() => {
        if (open) {
            setS(initial());
            setSourceKey(newSourceKey());
            setError("");
        }
    }, [open]);

    const canSave = !pending && !!s.asOfDate && s.earningYear > 2000;

    const save = async () => {
        if (!canSave) return;
        setPending(true);
        setError("");
        try {
            await api.post(
                `/workforce/employees/${employeeId}/vv/opening-state`,
                buildOpeningStatePayload({ ...s, isRevision, sourceKey }));
            toast.success(i18n.t("wf.vVOpeningStateDialog.openingVvStateSaved", "Opening VV state saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (_err) {
            setError(i18n.t("wf.vVOpeningStateDialog.couldNotSaveTheOpeningVv", "Could not save the opening VV state. Check the values and try again."));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vv-opening-dialog"
                            className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isRevision ? i18n.t("wf.vVOpeningStateDialog.updateOpeningState", "Update opening state")
                            : i18n.t("wf.vVOpeningStateDialog.setOpeningVvState", "Set opening VV state")}
                    </DialogTitle>
                    <DialogDescription>
                        {isRevision
                            ? i18n.t("wf.vVOpeningStateDialog.aNewRevisionIsRecordedEarlier", "A new revision is recorded. Earlier entries stay in the history.")
                            : i18n.t("wf.vVOpeningStateDialog.theEmployeeSVvPositionOn", "The employee's VV position on the as-of date.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-sm">
                            <span className="text-neutral-700">{i18n.t("wf.vVOpeningStateDialog.asOf", "As of")}</span>
                            <Input type="date" className="mt-1"
                                data-testid="vv-opening-as-of"
                                value={s.asOfDate}
                                onChange={(e) => set({ asOfDate: e.target.value })} />
                        </label>
                        <NumberField label={i18n.t("wf.vVOpeningStateDialog.earningYear", "Earning year")}
                            testId="vv-opening-earning-year"
                            value={s.earningYear}
                            onChange={(n) => set({ earningYear: n })} />
                    </div>
                    <div className="text-sm">
                        <span className="text-neutral-700">
                            {i18n.t("wf.vVOpeningStateDialog.eligibleHoursThisYear", "Eligible hours this year")}
                        </span>
                        <div className="mt-1 grid grid-cols-2 gap-3">
                            <NumberField label={i18n.t("wf.vVOpeningStateDialog.hours", "Hours")} testId="vv-opening-hours"
                                value={s.hours}
                                onChange={(n) => set({ hours: n })} />
                            <NumberField label={i18n.t("wf.vVOpeningStateDialog.minutes", "Minutes")}
                                testId="vv-opening-minutes" max={59}
                                value={s.minutes}
                                onChange={(n) => set({ minutes: n })} />
                        </div>
                    </div>
                    <NumberField label={i18n.t("wf.vVOpeningStateDialog.unusedVvFromPreviousYear", "Unused VV from previous year")}
                        testId="vv-opening-carried" max={9}
                        value={s.carriedCount}
                        onChange={(n) => set({ carriedCount: n })}
                        hint={i18n.t("wf.vVOpeningStateDialog.earnedLastYearAndNotYet", "Earned last year and not yet taken.")} />
                    <label className="block text-sm">
                        <span className="text-neutral-700">Note (optional)</span>
                        <Input className="mt-1" data-testid="vv-opening-reason"
                            value={s.reason}
                            onChange={(e) => set({ reason: e.target.value })} />
                    </label>
                    {error && (
                        <div role="alert" data-testid="vv-opening-error"
                            className="rounded-md border border-amber-200
                                        bg-amber-50 px-3 py-2 text-sm
                                        text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="vv-opening-cancel">
                        {i18n.t("common.cancel", "Cancel")}
                    </Button>
                    <Button onClick={save} disabled={!canSave}
                        data-testid="vv-opening-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
